<template>
	<div class="poster-container">
		<!-- Top Banner (Red) -->
		<div class="poster-top-banner" ref="topBanner">
			<div class="poster-logo" ref="logoContainer">
				<img 
					src="../assets/images/oliebolGeusje.jpeg" 
					alt="Logo" 
					ref="logoImage"
				/>
			</div>
			<div class="poster-top-text" ref="topTextContainer">
				<div class="poster-top-line1" ref="topLine1">Oliebollen bestellen,</div>
				<div class="poster-top-line2" ref="topLine2">scouting steunen</div>
			</div>
		</div>

		<div class="poster-divider" />

		<!-- Middle Section -->
		<div class="poster-middle-section">
			<!-- Left Half: Image Area -->
			<div class="poster-image-area">
				<img v-if="photoUrl" :src="photoUrl" alt="Uploaded photo" class="poster-photo" />
				<div v-else class="poster-photo-placeholder">
					<p>Upload een foto</p>
				</div>
			</div>

			<!-- Right Half: Text Area (Blue) -->
			<div class="poster-text-area">
				<div class="poster-greeting" ref="greetingContainer">
					<div ref="greetingLine1">Hoi! Ik ben {{ name || 'Naam' }}.</div>
					<div ref="greetingLine2">Jij koopt toch ook oliebollen?</div>
				</div>
			</div>
		</div>

		<div class="poster-divider" />

		<!-- Bottom Section -->
		<div class="poster-bottom-section">
			<!-- Left Half: Details Area (Red) -->
			<div class="poster-details-area" ref="detailsContainer">
				<div class="poster-detail-line" ref="detailLine1">10 stuks € 7,50</div>
				<div class="poster-detail-line" ref="detailLine2">31 december</div>
				<div class="poster-detail-line" ref="detailLine3">thuisbezorgd</div>
			</div>

			<div class="vertical-divider" />

			<!-- Right Half: QR Code Area (White) -->
			<div class="poster-qr-area">
				<qrcode-vue 
					v-if="qrUrl" 
					:value="qrUrl" 
					:size="qrSize" 
					level="H"
					class="poster-qr-code"
				/>
				<div v-else class="poster-qr-placeholder">
					<p>QR code wordt geladen...</p>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import QrcodeVue from "qrcode.vue";
import { getUser } from "../services/UserService";

export default {
	name: "PosterGenerator",
	components: {
		QrcodeVue
	},
	props: {
		name: {
			type: String,
			default: "Naam"
		},
		photo: {
			type: [String, File],
			default: null
		},
		userId: {
			type: String,
			default: null
		}
	},
	data() {
		return {
			photoUrl: null,
			qrUrl: null,
			qrSize: 200,
			currentUserId: null
		};
	},
	watch: {
		photo: {
			handler(newPhoto) {
				this.handlePhotoChange(newPhoto);
			},
			immediate: true
		},
		userId: {
			handler(newUserId) {
				if (newUserId) {
					this.currentUserId = newUserId;
					this.generateQrUrl();
				}
			},
			immediate: true
		}
	},
	methods: {
		handlePhotoChange(photo) {
			if (!photo) {
				this.photoUrl = null;
				return;
			}

			if (typeof photo === 'string') {
				// If it's already a URL string
				this.photoUrl = photo;
			} else if (photo instanceof File) {
				// If it's a File object, create object URL
				const reader = new FileReader();
				reader.onload = (event) => {
					this.photoUrl = event.target.result;
				};
				reader.readAsDataURL(photo);
			}
		},
		generateQrUrl() {
			if (this.currentUserId) {
				this.qrUrl = `https://de-geuzen.nl/webshop?lid=${this.currentUserId}`;
			}
		},
		async loadUserId() {
			if (!this.userId) {
				try {
					const userData = await getUser();
					if (userData && userData.user) {
						this.currentUserId = userData.user;
						this.generateQrUrl();
					}
				} catch (error) {
					console.error('Error loading user ID:', error);
				}
			} else {
				this.currentUserId = this.userId;
				this.generateQrUrl();
			}
		},
	},
	mounted() {
		this.loadUserId();
	}
};
</script>

<style scoped>
.vertical-divider {
	background-color: #b4392b;
	height: 100%;
	width: 1em;
}
.poster-divider {
	background-color: #b4392b;
	height: 1em;
	width: 100%;
}
.poster-container {
	width: 100%;
	max-width: 800px;
	margin: 0 auto;
	background: white;
	box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

/* Top Banner (Red) */
.poster-top-banner {
	background-color: #FFF;
	color: #b4392b;
	display: flex;
	justify-content: flex-end;
	align-items: stretch; /* Stretch to fill height */
	position: relative;
	padding: 1% 3% 1% 0; /* Minimal vertical padding, more horizontal */
	width: 100%;
	/* Allow banner to grow based on content - no min-height constraint */
}

.poster-top-text {
	font-family: 'Brush Script MT', cursive, sans-serif;
	font-size: 5rem; /* Fixed font size - no resizing */
	line-height: 0.6; /* Tighter line height */
	display: flex;
	flex-direction: column;
	justify-content: space-between; /* Space from top to bottom */
	align-items: flex-end; /* Right align text */
	text-align: right;
	width: auto; /* Let it size based on content */
	margin-right: 2%;
	height: 100%; /* Fill parent height */
	align-self: stretch; /* Stretch to fill banner height */
}

.poster-top-line1 {
	margin-bottom: 0; /* No margin - lines should be close together */
	margin-top: 0;
	white-space: nowrap;
	display: block;
	text-align: right;
	line-height: 0.6; /* Tight line height */
}

.poster-top-line2 {
	margin-left: 0;
	margin-bottom: 0;
	margin-top: 0;
	white-space: nowrap;
	display: block;
	text-align: right;
	padding-right: 0;
	line-height: 0.6; /* Tight line height */
}

.poster-logo {
	flex-shrink: 0;
	margin-left: 2%;
	order: 2; /* Logo comes after text */
	transition: width 0.3s ease, height 0.3s ease; /* Smooth size transitions */
}

.poster-logo img {
	width: 100%;
	height: 100%;
	object-fit: contain;
}

.poster-logo-placeholder {
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 0.8rem;
	opacity: 0.7;
}

/* Middle Section */
.poster-middle-section {
	display: flex;
	height: 300px;
}

/* Left Half: Image Area */
.poster-image-area {
	flex: 1;
	background: #f5f5f5;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
}

.poster-photo {
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.poster-photo-placeholder {
	text-align: center;
	color: #999;
	padding: 20px;
}

/* Right Half: Text Area (Blue) */
.poster-text-area {
	flex: 1;
	background-color: #003366;
	color: #FFF;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 1.6em;
}

.poster-greeting {
	font-size: 2.2em;
	font-weight: 500;
	line-height: 1.6;
	text-align: left;
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	justify-content: center;
}

.poster-greeting > div {
	width: 100%;
}

/* Bottom Section */
.poster-bottom-section {
	display: flex;
	height: 250px;
}

/* Left Half: Details Area (Red) */
.poster-details-area {
	flex: 1;
	font-weight: bold;
	background-color: #FFF;
	color: #1e3c7c;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: flex-start;
	padding: 2em;
	font-size: 2.2em;
	line-height: 1.0;
	width: 100%;
	height: 100%;
}

.poster-detail-line {
	margin-bottom: 10px;
	width: 100%;
	white-space: nowrap;
}

/* Right Half: QR Code Area (White) */
.poster-qr-area {
	flex: 1;
	background-color: white;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 20px;
}

.poster-qr-code {
	display: block;
}

.poster-qr-placeholder {
	text-align: center;
	color: #999;
}

/* Responsive adjustments */
@media (max-width: 600px) {
	.poster-top-text {
		font-size: 1.8rem;
	}

	.poster-middle-section {
		flex-direction: column;
		height: auto;
	}

	.poster-image-area {
		height: 200px;
	}

	.poster-text-area {
		height: 150px;
	}

	.poster-greeting {
		font-size: 1.2rem;
	}

	.poster-bottom-section {
		flex-direction: column;
		height: auto;
	}

	.poster-details-area {
		height: 150px;
	}

	.poster-qr-area {
		height: 200px;
	}

	.poster-qr-code {
		width: 150px;
		height: 150px;
	}
}
</style>